import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    IconButton,
    CircularProgress,
    Stack,
    Alert,
    Tooltip,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Person as PersonIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface User {
    id: number;
    username: string;
    created_at: string;
}

const UsersPage = () => {
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        username: '',
        password: '',
    });
    const [error, setError] = useState('');

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await api.get('/auth/users');
            setUsers(response.data);
        } catch (error) {
            console.error('Kullanıcılar alınırken hata:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = () => {
        setForm({ username: '', password: '' });
        setError('');
        setOpen(true);
    };

    const handleCloseDialog = () => {
        setOpen(false);
    };

    const handleSubmit = async () => {
        if (!form.username || !form.password) {
            setError('Kullanıcı adı ve şifre gereklidir');
            return;
        }

        try {
            await api.post('/auth/register', form);
            fetchUsers();
            handleCloseDialog();
        } catch (err: any) {
            setError(err.response?.data?.error || 'Kullanıcı oluşturulurken hata oluştu');
        }
    };

    const [deleteOpen, setDeleteOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<number | null>(null);

    const handleOpenDeleteDialog = (id: number) => {
        setUserToDelete(id);
        setDeleteOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setDeleteOpen(false);
        setUserToDelete(null);
    };

    const confirmDeleteUser = async () => {
        if (!userToDelete) return;

        try {
            await api.delete(`/auth/users/${userToDelete}`);
            fetchUsers();
            handleCloseDeleteDialog();
        } catch (error) {
            console.error('Kullanıcı silinirken hata:', error);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" component="h1">
                    Kullanıcı Yönetimi
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenDialog}
                >
                    Yeni Kullanıcı
                </Button>
            </Box>

            <Paper elevation={0}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Kullanıcı Adı</TableCell>
                                <TableCell>Oluşturulma Tarihi</TableCell>
                                <TableCell align="right">İşlemler</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow key={user.id}>
                                    <TableCell>
                                        <Box display="flex" alignItems="center">
                                            <PersonIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                                            {user.username}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {new Date(user.created_at).toLocaleDateString('tr-TR')}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Sil">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleOpenDeleteDialog(user.id)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>Yeni Kullanıcı Ekle</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {error && <Alert severity="error">{error}</Alert>}
                        <TextField
                            label="Kullanıcı Adı"
                            fullWidth
                            value={form.username}
                            onChange={(e) => setForm({ ...form, username: e.target.value })}
                        />
                        <TextField
                            label="Şifre"
                            type="password"
                            fullWidth
                            value={form.password}
                            onChange={(e) => setForm({ ...form, password: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>İptal</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        Ekle
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            < Dialog open={deleteOpen} onClose={handleCloseDeleteDialog} >
                <DialogTitle>Kullanıcı Sil</DialogTitle>
                <DialogContent>
                    <Typography>Bu kullanıcıyı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog}>İptal</Button>
                    <Button onClick={confirmDeleteUser} color="error" variant="contained">
                        Sil
                    </Button>
                </DialogActions>
            </Dialog >
        </>
    );
};

export default UsersPage;
